/* eslint-disable react/destructuring-assignment */
import React, { useMemo, useState } from 'react';
import StringUtil from '@leverege/string-util';
import { Config } from '@leverege/plugin';
import { TabPane, Popup, NumericInput, DateTimeRangePicker, IdGen, PropertyGrid, TextInput, Content } from '@leverege/ui-elements';
import { useValueChange } from '@leverege/ui-hooks';
import { ModelEditorFactory } from '@leverege/ui-plugin';
import I18N from '@leverege/i18n';
import { LogicCondition } from '@leverege/condition';
import { R } from '@leverege/value-resolver';
import DataViewers from "../../DataViewers.js";
import Model from "../models/HistoryViewModel.js";
import S from './HistoryViewEditor.css';
import ConditionFilterModels from "../../../filters/filter/ConditionFilterModels.js";
import HistoryConditionEditor from "./HistoryConditionEditor.js";
import DataViewerCardModel from "../../dashboard/DataViewerCardModel.js";
import SortModels from "../../../filters/sort/SortModels.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t
} = I18N.ns('molten.dataViewers.history.HistoryViewEditor');
export default function HistoryViewEditor(props) {
  const {
    value,
    eventData,
    objectType,
    delegate,
    dataViewerCardModel,
    relationship
  } = props;
  const [ids] = useState(IdGen.createIds('lItem', 4));
  const onChange = useValueChange(Model, props);
  const onChangeQueryMode = evt => {
    const nModel = Model.setQueryMode(value, evt.data, !evt.value);
    props.onChange({
      value: nModel,
      data: eventData
    });
  };
  const range = Model.getRange(value);
  const queryModes = Model.getQueryModes(value);
  const isForItem = Model.getIsForItem(value);
  const needsDate = dataViewerCardModel && DataViewerCardModel.getNeedsDate(dataViewerCardModel);
  const needsCondition = dataViewerCardModel && DataViewerCardModel.getNeedsCondition(dataViewerCardModel);
  const needsOrder = dataViewerCardModel && DataViewerCardModel.getNeedsOrder(dataViewerCardModel);
  const perPageEditable = Model.getPerPageEditable(value);
  const condition = useMemo(() => {
    return Model.getCondition(value) || ConditionFilterModels.getModelFor(delegate.type, objectType)?.create({
      rootCondition: LogicCondition.create({
        operator: 'and'
      })
    }) || null;
  }, [value, delegate.type, objectType]);
  const order = useMemo(() => {
    return Model.getOrder(value) || SortModels.getSortModelFor(delegate.type, objectType)?.create() || null;
  }, [value, delegate.type, objectType]);
  const timeValue = useMemo(() => {
    if (typeof range === 'string') {
      return range;
    }
    const ret = {
      ...range
    };
    if (ret.start != null && !(ret.start instanceof Date)) {
      ret.start = new Date(ret.start);
    }
    if (ret.end != null && !(ret.end instanceof Date)) {
      ret.end = new Date(ret.end);
    }
    return ret;
  }, [range]);
  let subEditors = [];
  if (Array.isArray(value?.dataViewers)) {
    const hasBreakpoints = value.dataViewers.length > 1;
    subEditors = value.dataViewers.map((dv, idx) => {
      if (dv.settings == null) {
        return null;
      }
      const dataViewer = DataViewers.get(dv);
      const eProps = {
        ...props,
        value: dv.settings,
        onChange: evt => {
          const nValue = Model.setDataViewerSettings(value, evt.data, evt.value);
          props.onChange({
            value: nValue,
            data: eventData
          });
        },
        eventData: idx,
        objectType
      };
      const editor = ModelEditorFactory.create(dv.settings, eProps);
      if (editor == null) {
        return null;
      }
      const brk = hasBreakpoints && idx > 0 && value.breakpoints[idx - 1];
      const tabName = R(dataViewer?.editorName || dataViewer?.name || t('editor'));
      return /*#__PURE__*/_jsxs(TabPane.Tab, {
        tabName: typeof tabName === 'function' ? tabName() : tabName,
        tabId: dv.id || idx,
        children: [hasBreakpoints && idx > 0 && /*#__PURE__*/_jsxs(Content, {
          children: [/*#__PURE__*/_jsx(Content.Area, {
            style: {
              overflow: 'hidden'
            },
            children: editor
          }), /*#__PURE__*/_jsx(Content.Footer, {
            children: /*#__PURE__*/_jsx(PropertyGrid, {
              inline: true,
              children: /*#__PURE__*/_jsx(PropertyGrid.Item, {
                newRow: true,
                label: t('widthGreater', {
                  tabName
                }),
                children: /*#__PURE__*/_jsx(TextInput, {
                  value: typeof brk === 'number' ? `${brk}` : brk,
                  eventData: idx - 1,
                  onChange: evt => {
                    let v = parseInt(evt.value);
                    // eslint-disable-next-line no-restricted-globals
                    if (isNaN(v)) {
                      v = evt.value;
                    }
                    const nValue = Model.setBreakpoint(value, evt.data, v);
                    props.onChange({
                      value: nValue,
                      data: eventData
                    });
                  }
                })
              })
            })
          })]
        }), !(hasBreakpoints && idx > 0) && editor]
      }, dv.id || idx);
    });
  }
  const editors = [];
  if (perPageEditable) {
    editors.push(/*#__PURE__*/_jsx(PropertyGrid.Item, {
      newRow: true,
      label: t('perPage'),
      children: /*#__PURE__*/_jsx(NumericInput, {
        max: Math.min(Config.get('DeviceList', 'maxItemsPerPage', 500), 500),
        value: Model.getPerPage(value),
        eventData: "setPerPage",
        onChange: onChange
      })
    }, "perPage"));
  }
  if (!isForItem) {
    editors.push(/*#__PURE__*/_jsx(PropertyGrid.Item, {
      newRow: true,
      label: t('queryMode'),
      children: /*#__PURE__*/_jsxs(Popup, {
        title: getQueryModeLabel(queryModes),
        children: [/*#__PURE__*/_jsx(Popup.Item, {
          value: queryModes.targeted,
          eventData: "targeted",
          onClick: onChangeQueryMode,
          checked: queryModes.targeted,
          checkStyle: "check",
          children: t('targeted')
        }, ids[1]), /*#__PURE__*/_jsx(Popup.Item, {
          value: queryModes.selected,
          eventData: "selected",
          onClick: onChangeQueryMode,
          checked: queryModes.selected,
          checkStyle: "check",
          children: t('selected')
        }, ids[2]), /*#__PURE__*/_jsx(Popup.Item, {
          value: queryModes.all,
          eventData: "all",
          onClick: onChangeQueryMode,
          checked: queryModes.all,
          checkStyle: "check",
          children: t('all')
        }, ids[3])]
      })
    }, "queryMode"));
  }
  if (!needsDate) {
    editors.push(/*#__PURE__*/_jsx(PropertyGrid.Item, {
      newRow: true,
      label: t('queryRange'),
      children: /*#__PURE__*/_jsx(DateTimeRangePicker, {
        value: timeValue,
        eventData: "setRange",
        onChange: onChange
      })
    }, "range"));
  }
  if (!needsCondition) {
    editors.push(/*#__PURE__*/_jsx(PropertyGrid.Item, {
      newRow: true,
      label: t('condition'),
      children: /*#__PURE__*/_jsx(HistoryConditionEditor, {
        ...props,
        eventData: "setCondition",
        value: condition,
        onChange: onChange
      })
    }, "condition"));
  }
  if (!needsOrder) {
    editors.push(/*#__PURE__*/_jsx(PropertyGrid.Item, {
      newRow: true,
      label: t('order'),
      children: ModelEditorFactory.create(order, {
        context: {
          objectType
        },
        relationship,
        delegate,
        eventData: 'setOrder',
        onChange
      })
    }, "order"));
  }
  return /*#__PURE__*/_jsxs(TabPane, {
    className: S.editor,
    children: [subEditors, /*#__PURE__*/_jsx(TabPane.Tab, {
      tabName: t('query'),
      tabId: "options",
      children: /*#__PURE__*/_jsx("div", {
        className: S.pad,
        children: /*#__PURE__*/_jsxs(PropertyGrid, {
          inline: true,
          children: [/*#__PURE__*/_jsx(PropertyGrid.Header, {
            title: t('title')
          }), editors]
        })
      })
    })]
  });
}
function getQueryModeLabel(queryModes) {
  return Object.entries(queryModes).reduce((prev, [key, value]) => {
    if (value) {
      return [...prev, StringUtil.capitalize(key)];
    }
    return prev;
  }, []).join(' & ');
}